import type { Dispatch, FC, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { SharedValue } from 'react-native-reanimated';
import { useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { isNative } from '@/shared/utils/platform';

export type MeinOrtScrollContextValue = {
  scrollY: SharedValue<number>;
  feedInset: number;
  headerHeight: number;
  setHeaderHeight: Dispatch<SetStateAction<number>>;
};

export const MeinOrtScrollContext = createContext<
  MeinOrtScrollContextValue | undefined
>(undefined);

export const MeinOrtScrollProvider: FC<PropsWithChildren> = ({ children }) => {
  const insets = useSafeAreaInsets();
  const topInset = isNative ? insets.top : 0;
  const scrollY: SharedValue<number> = useSharedValue(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  const context = useMemo<MeinOrtScrollContextValue>(
    () => ({
      scrollY,
      feedInset: topInset + headerHeight,
      headerHeight,
      setHeaderHeight
    }),
    [scrollY, topInset, headerHeight]
  );

  return (
    <MeinOrtScrollContext value={context}>{children}</MeinOrtScrollContext>
  );
};

export const useMeinOrtScroll = () => {
  const context = useContext(MeinOrtScrollContext);
  if (context === undefined) {
    throw new Error(
      'useMeinOrtScroll must be used within MeinOrtScrollContext'
    );
  }
  return context;
};
